/**
 * „Stały link do rozmów": its read (shared with `/rezerwacje`'s prefill), the field it is changed in,
 * `PUT` and `DELETE /me/standing-call-link`. Sessions that already copied the link keep theirs, so
 * removing it asks nothing. What the row draws is `model/standingCallLink`'s.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { standingCallLinkQueryKey, standingCallLinkQueryOptions } from '~/entities/standing-call-link';
import {
  deleteMeStandingCallLink,
  putMeStandingCallLink,
} from '~/shared/api/generated/endpoints/my-standing-call-link/my-standing-call-link';
import type { TrainWithMeModulesTrainingDtosResponsesStandingCallLinkResponse as StandingCallLink } from '~/shared/api/generated/model';
import { isApplePlatform } from '~/shared/lib/applePlatform';
import { httpsLinkOf } from '~/shared/lib/httpsLink';
import { useAnnouncer } from '~/shared/lib/useAnnouncer';

import { standingCallLinkView, type StandingCallLinkView } from '../model/standingCallLink';

const KEY = 'web:account.calendar.standing';

export interface StandingCallLinkApi {
  /** `null` until the read answered; a failed read draws nothing. */
  readonly view: StandingCallLinkView | null;
  readonly field: { readonly value: string; readonly problem: string | null } | null;
  readonly busy: boolean;
  readonly refusal: string | null;
  readonly announcement: string;
  readonly onOpen: () => void;
  readonly onEdit: (value: string) => void;
  readonly onClose: () => void;
  readonly onSave: () => void;
  readonly onRemove: () => void;
}

/** @param active whether the Integracje tab is on screen. */
export function useStandingCallLink(active: boolean): StandingCallLinkApi {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { message: announcement, announce } = useAnnouncer();
  const read = useQuery({ ...standingCallLinkQueryOptions(), enabled: active });
  const [field, setField] = useState<{ value: string; tried: boolean } | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);

  const landed = (answer: StandingCallLink, done: 'saved' | 'removed') => {
    queryClient.setQueryData(standingCallLinkQueryKey, answer);
    setField(null);
    setRefusal(null);
    announce(t(`${KEY}.done.${done}`));
  };
  const failed = () => {
    setRefusal(t(`${KEY}.failed`));
  };
  const save = useMutation({
    mutationFn: (link: string) => putMeStandingCallLink({ link }),
    onSuccess: (answer) => {
      landed(answer, 'saved');
    },
    onError: failed,
  });
  const remove = useMutation({
    mutationFn: () => deleteMeStandingCallLink(),
    onSuccess: () => {
      landed({ link: null }, 'removed');
    },
    onError: failed,
  });
  const busy = save.isPending || remove.isPending;
  const typed = field?.value.trim() ?? '';
  const valid = httpsLinkOf(typed) !== null;

  return {
    view: read.data === undefined ? null : standingCallLinkView(read.data.link, isApplePlatform(globalThis.navigator)),
    field: field === null ? null : { value: field.value, problem: field.tried && !valid ? t(`${KEY}.invalid`) : null },
    busy,
    refusal,
    announcement,
    onOpen: () => {
      if (busy) return;
      setRefusal(null);
      setField({ value: read.data?.link ?? '', tried: false });
    },
    onEdit: (value) => {
      setField((current) => (current === null ? current : { ...current, value }));
    },
    onClose: () => {
      if (!busy) setField(null);
    },
    onSave: () => {
      if (busy || field === null) return;
      if (!valid) {
        setField({ ...field, tried: true });
        return;
      }
      save.mutate(typed);
    },
    onRemove: () => {
      if (busy) return;
      setRefusal(null);
      remove.mutate();
    },
  };
}
